// @ts-nocheck
import { useRef } from 'react';
import { AlertTriangle, ChevronRight, Upload } from 'lucide-react';
import { VR_BTN_PRIMARY } from '../shared/voteRoomConstants';
import { ManualEntryRow } from './ManualEntryRow';

/**
 * First wizard step: file upload (drag-drop / click) + manual entry list. All async persistence is
 * owned by the parent `SetupPage`; this component is presentation + drag/drop state only.
 */
export const ImportShareholdersStep = ({
  locked,
  dragOver,
  setDragOver,
  onFile,
  uploadError,
  analysisId,
  saveStatus,
  shareholders,
  newRow,
  setNewRow,
  manualEntryError,
  onAddRow,
  onRequestRemoveRow,
  onContinue,
}) => {
  const fileRef = useRef(null);
  return (
    <div className="space-y-4">
      <div
        role="button"
        tabIndex={locked ? -1 : 0}
        aria-label="Upload cap table CSV or TSV file"
        aria-disabled={locked}
        className={`border-2 border-dashed rounded-xl px-6 py-5 text-center transition-colors ${
          locked
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
            : `cursor-pointer ${dragOver ? 'border-emerald-400 bg-emerald-50/40' : 'border-gray-200 hover:border-emerald-200 hover:bg-emerald-50/20 bg-white'}`
        }`}
        onDragOver={(e) => {
          if (locked) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (locked) return;
          e.preventDefault();
          setDragOver(false);
          onFile(e.dataTransfer.files[0]);
        }}
        onClick={() => {
          if (locked) return;
          fileRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (locked) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileRef.current?.click();
          }
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.tsv,.txt"
          className="hidden"
          disabled={locked}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = '';
          }}
        />
        <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50">
          <Upload size={18} className="text-emerald-600" />
        </div>
        <p className="text-xs font-semibold text-gray-900">Upload Cap Table</p>
        <p className="text-xs text-gray-500 mt-0.5">Drag & drop a CSV or TSV file, or click to browse</p>
      </div>

      {uploadError && (
        <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-2.5 text-xs text-red-700 flex items-start space-x-2">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-red-400" />
          <span>{uploadError}</span>
        </div>
      )}

      {analysisId && (saveStatus === 'saving' || saveStatus === 'error') && (
        <div
          className={`border rounded-lg px-4 py-2 text-xs ${
            saveStatus === 'error'
              ? 'bg-red-50 border-red-100 text-red-700'
              : 'bg-amber-50 border-amber-100 text-amber-700'
          }`}
        >
          {saveStatus === 'saving' ? 'Saving shareholders...' : 'Could not save shareholders. Please try again.'}
        </div>
      )}

      <ManualEntryRow
        newRow={newRow}
        setNewRow={setNewRow}
        shareholders={shareholders}
        locked={locked}
        manualEntryError={manualEntryError}
        onAdd={onAddRow}
        onRequestRemove={onRequestRemoveRow}
      />

      {shareholders.length > 0 && (
        <div className="flex justify-end">
          <button type="button" onClick={onContinue} className={VR_BTN_PRIMARY}>
            Review Shareholders <ChevronRight size={13} className="ml-1.5" aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
};
